import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { IPossibleTableKey, TableWithFields } from '@types'
import { Select } from '@ss/meta-core-components'
import { config } from '../../config'
import { KeyOption } from './KeyOption'
import useRest from '../../hooks/useRest'
import { CloseIcon } from '../../assets/icons/closeIcon'
import { useLocation } from 'react-router-dom'

interface Props {
  setParams: React.Dispatch<React.SetStateAction<TableWithFields>>
  setOpenSidebar?: () => void
  openSidebar?: boolean
  setSelectedIndex: React.Dispatch<React.SetStateAction<string | undefined>>
  selectedIndex: string | undefined
}

/**
 * Componente Sidebar per il template ChangeTableKey
 * @param props.SetParams {Function=} setter per i filtri della pagina
 * @param props.setOpenSidebar {Function=} azione del bottone 'Modifica attributi analizzati'
 * @param props.openSidebar {boolean} flag per l'apertura della sidebar
 * @param props.setSelectedIndex {Function=} setter per l'indice selezionato
 * @param props.selectedIndex {string  | undefined} indeice selezionato
 * @component
 */

export const Sidebar: React.FC<Props> = (props) => {
  const {
    setParams,
    setOpenSidebar,
    openSidebar = false,
    setSelectedIndex,
    selectedIndex,
  } = props

  const [selected, setSelected] = useState<IPossibleTableKey[]>([])
  const [keys, setKeys] = useState<IPossibleTableKey[]>([])

  const location = useLocation()

  const { post } = useRest<{ data: IPossibleTableKey[] }>('/get_fields')

  useEffect(() => {
    const newKeys = selected.map(({ name, type }) => ({
      value: `${name}`,
      type,
    }))
    setParams((old) => ({ ...old, keys: newKeys }))
  }, [selected])

  useEffect(() => {
    //request per i campi possibili
    post({ index: selectedIndex, page: location.pathname.substring(1) }).then(
      ({ data }) => {
        const newSelected: IPossibleTableKey[] = []
        const newAll: IPossibleTableKey[] = []

        data.forEach((key) => {
          if (key.selected) newSelected.push(key)
          else newAll.push(key)
        })
        //setter
        setKeys(newAll)
        setSelected(newSelected)
      }
    )
  }, [selectedIndex])

  return (
    <SidebarContaioner
      data-cy={`add-key-sidebar`}
      width={openSidebar ? '315px' : '0px'}
      show={openSidebar}
    >
      <Content>
        <TitleContainer>
          <Text>SELEZIONA INDICE</Text>
          <CloseSidebar
            data-cy={'close-sidebar'}
            onClick={() => setOpenSidebar && setOpenSidebar()}
          >
            <CloseIcon />
          </CloseSidebar>
          <Select
            dataCy={'index-select'}
            minWidth={'0px'}
            initialValue={[config.dataSet.indexes[0]]}
            options={[
              ...config.dataSet.indexes,
              ...(location.pathname === '/correlation'
                ? [
                    {
                      label: 'infocamere_macro',
                      value: 'infocamere_macro',
                    },
                  ]
                : []),
            ]}
            onChange={(data) => {
              setSelectedIndex(
                data?.[0].value ? `${data?.[0].value}` : undefined
              )
              setParams((old) => ({
                ...old,
                index: (data?.[0]?.value as string) || '',
              }))
            }}
          />
        </TitleContainer>
        <OptionContainer>
          <Text data-cy={'sidebar-selected-label'}>
            Attributi visibili{' (' + selected.length + '/10)'}
          </Text>
          <List data-cy={'sidebar-selected-list'}>
            {selected.map((opt, index) => (
              <KeyOption
                show={openSidebar}
                option={opt}
                key={`selected-${index}`}
                selected
                onClick={(option) => {
                  setSelected((old) => [
                    ...old.filter((k) => k.name !== option.name),
                  ])
                  setKeys((old) => [...old, option])
                }}
              />
            ))}
          </List>
        </OptionContainer>
        <OptionContainer>
          <Text data-cy={'sidebar-all-label'}>
            Attributi non visibili{' (' + keys.length + ')'}
          </Text>
          <List data-cy={'sidebar-all-list'}>
            {keys.map((opt, index) => (
              <KeyOption
                show={openSidebar}
                option={opt}
                key={`all-${index}`}
                onClick={(option) => {
                  if (selected.length === 10) return
                  setSelected((old) => [...old, option])
                  setKeys((old) => [
                    ...old.filter((k) => k.name !== option.name),
                  ])
                }}
              />
            ))}
          </List>
        </OptionContainer>
      </Content>
    </SidebarContaioner>
  )
}

//region STYLE

const SidebarContaioner = styled.div<{
  width: string
  show: boolean
}>`
  position: absolute;
  width: ${({ width }) => width};
  transition: left 0.2s ease-in-out, width 0.4s ease-in-out 190ms;
  background: var(--card-background);
  height: max-content;
  min-height: 100vh;
  z-index: 1500;
  left: 0;
  top: 0;
  filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));
  display: grid;

  & * {
    ${({ show }) => !show && 'opacity: 0'};
    ${({ show }) => !show && 'padding: 0'};
    transition: ${({ show }) =>
      show ? 'opacity 200ms linear 400ms' : 'opacity 200ms linear'};
    max-width: ${({ width }) => width} !important;
  }
`

const Content = styled.div`
  padding: 20px 0 20px 20px;
  display: grid;
  grid-template-rows: 70px 320px auto;
  min-height: 100%;
  height: 100vh;
  gap: 35px;
  overflow-y: auto;

  //Scroll restyled
  ::-webkit-scrollbar-track {
    background-color: var(--background-dark-blue);
  }

  ::-webkit-scrollbar {
    width: 10px;
    height: 10px;
    background-color: var(--secondary);
  }

  ::-webkit-scrollbar-corner {
    background-color: var(--secondary);
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 8px;
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    background-color: var(--focus);
  }
`
const TitleContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 0.5fr;
  grid-template-rows: 0.8fr 1fr;
`
const Text = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
`
const CloseSidebar = styled.div`
  cursor: pointer;
  place-self: start center;
`
const OptionContainer = styled.div`
  padding-right: 20px;
  display: grid;
  grid-template-rows: 20px auto;
  gap: 5px;
`
const List = styled.div`
  border-top: 1px solid #ffffff;

  & > :nth-child(even) {
    background: var(--primary);
  }
`
//endregion
